import React from "react";
import { Container, Reveal, HairlineList, Overline } from "@/components/primitives";
import FaqSection from "@/components/FaqSection";
import { caseminderFaqs } from "@/content/faqs";
import { useDocumentMeta } from "@/lib/meta";
import {
  ProductHero,
  ContentSection,
  AdjacentCrossSell,
  ProductFinalCTA,
} from "@/components/ProductSections";

const IMG = "https://images.pexels.com/photos/4412921/pexels-photo-4412921.jpeg";

export default function CaseMinder() {
  useDocumentMeta({
    fullTitle: "Case management for small law firms on Microsoft 365 | CaseMinder",
    description:
      "Case management for small law firms that keeps Outlook and SharePoint: an Outlook add-in for filing, deadlines and time recording, a fixed new-matter chain from conflict check to client care letter, and documents in your own SharePoint.",
    path: "/caseminder",
  });
  return (
    <div data-testid="caseminder-page">
      <ProductHero
        testid="caseminder-hero"
        eyebrow="CaseMinder · The operating layer"
        title="Run the firm. Do not rent it from your software."
        subhead="CaseMinder gives law firm owners a live operational spine for matters, workflows and oversight, without the opacity, lock-in and generic bloat of traditional legal SaaS."
        primaryLabel="Book a CaseMinder walkthrough"
        secondaryLabel="See how it fits your firm"
        secondaryTo="#adjacent"
        image={IMG}
      />

      {/* Ownership — core pillar, front and centre */}
      <section className="border-b border-border py-24 md:py-32" data-testid="caseminder-ownership">
        <Container>
          <Reveal>
            <div className="max-w-3xl">
              <Overline accent>The commercial difference · Ownership</Overline>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[0.98] tracking-[-0.03em] text-foreground md:text-6xl">
                Own the system. Own the data.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                Law firms are tired of paying per user, per month for software that still does not
                really fit — then facing more pain when they want to leave. CaseMinder is built the
                other way round. A one-off build cost creates the system around your firm; after
                that, the firm owns its data and its working model.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 border-l border-t border-border sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                t: "One-off build cost",
                d: "Pay to build the system around your firm — not a rising per-seat rent forever.",
              },
              { t: "You own your data", d: "The firm owns its data and its working model, end to end." },
              {
                t: "Documents live in SharePoint",
                d: "Your files stay in your own Microsoft ecosystem, under your control.",
              },
              {
                t: "No per-seat subscription trap",
                d: "No pricing that punishes you for growing the team.",
              },
              {
                t: "No vendor black box",
                d: "You can inspect how the firm actually runs — nothing hidden behind a vendor wall.",
              },
              {
                t: "No ransom data export",
                d: "Leaving never means being held to ransom for access to your own data.",
              },
            ].map((c, i) => (
              <div
                key={i}
                className="flex h-full flex-col border-b border-r border-border p-7"
                data-testid={`caseminder-ownership-${i}`}
              >
                <h3 className="font-display text-lg font-bold tracking-[-0.02em] text-foreground">{c.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Your roadmap — the differentiator */}
      <section className="border-b border-border py-24 md:py-32" data-testid="caseminder-roadmap">
        <Container>
          <Reveal>
            <div className="max-w-3xl">
              <Overline accent>Your roadmap, not a vendor's</Overline>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[0.98] tracking-[-0.03em] text-foreground md:text-6xl">
                The roadmap is your firm's need.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                Generic legal software parks what you actually need "on the roadmap" — behind a queue of
                other firms and a vendor's release schedule. CaseMinder works the other way round. A team
                member flags a real need, and it moves through discovery, build and test into their daily
                experience — ridiculously quickly. Tailored down to the individual fee earner and the board.
              </p>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 border-l border-t border-border sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: "01", t: "A need is flagged", d: "A fee earner or director raises a real operational need — not a ticket in a vendor backlog." },
              { n: "02", t: "Discovery", d: "We work out how the team actually needs it to behave, in the context of your firm." },
              { n: "03", t: "Build & test", d: "It's built around your workflow and tested against how you really work." },
              { n: "04", t: "In daily use", d: "It lands in the daily experience — fast. The list of what's live keeps growing." },
            ].map((s) => (
              <div key={s.n} className="flex h-full flex-col border-b border-r border-border p-6" data-testid={`caseminder-roadmap-step-${s.n}`}>
                <span className="font-display text-sm font-bold tracking-widest text-accent">{s.n}</span>
                <h3 className="mt-6 font-display text-xl font-bold tracking-[-0.02em] text-foreground">{s.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* 1. Positioning */}
      <ContentSection
        n="01"
        eyebrow="Positioning"
        title="Not another practice management box"
        testid="caseminder-positioning"
      >
        <p className="text-lg leading-relaxed text-foreground">
          Most practice management systems arrive as finished boxes and expect your teams to adapt
          to them. CaseMinder works the other way round.
        </p>
        <p className="mt-5 leading-relaxed text-muted-foreground">
          It is developed with the firm, around how each team actually works, what creates the
          least friction, and how to make compliance the by-product of efficient work rather than
          the sword hanging over everyone's head.
        </p>
      </ContentSection>

      {/* 2. Built around your firm */}
      <ContentSection
        n="02"
        eyebrow="Discovery-led"
        title="Developed with you, not imposed on you"
        testid="caseminder-built"
      >
        <p className="leading-relaxed text-muted-foreground">
          CaseMinder starts with discovery. How does the team actually open files, move matters
          forward, communicate internally, collect risk information, record notes, manage
          completions and stay on top of deadlines?
        </p>
        <p className="mt-5 leading-relaxed text-muted-foreground">
          That real working pattern is then turned into a bespoke system. The result is not a
          generic workflow somebody thought law firms ought to use. It is your firm's workflow,
          made visible, auditable and easier to run.
        </p>
        <HairlineList
          testid="caseminder-built-list"
          items={[
            "Bespoke matter-opening and workflow design",
            "Path of least resistance to maximum compliance",
            "Supports staff instead of slowing them down",
            "Features built to the firm's real needs, not parked on a roadmap",
          ]}
        />
      </ContentSection>

      {/* Departments work differently */}
      <ContentSection
        n="03"
        eyebrow="Built around your teams"
        title="Departments work differently. Your system should too."
        testid="caseminder-departments"
      >
        <p className="text-lg leading-relaxed text-foreground">
          Conveyancers, litigators and other teams have very different needs. CaseMinder is built
          around your teams, so each department gets workflows that fit how it actually works.
        </p>
        <p className="mt-5 leading-relaxed text-muted-foreground">
          Instead of one generic workflow that forces workarounds, avoidance, or trying to jam one
          team's reality into another's, each department runs on a process shaped for it — while
          owners still get one consistent view across the whole firm.
        </p>
        <HairlineList
          testid="caseminder-departments-list"
          items={[
            "Conveyancing workflows built for conveyancing",
            "Litigation workflows built for litigation",
            "Each team's real process, not a lowest-common-denominator template",
            "One firm-wide view for owners across every department",
          ]}
        />
      </ContentSection>

      {/* 4. What it does */}
      <ContentSection
        n="04"
        eyebrow="Capability"
        title="The operational umbrella across the firm"
        testid="caseminder-what"
      >
        <p className="leading-relaxed text-muted-foreground">
          CaseMinder takes the best of the tools the firm already uses and puts an owner-controlled
          operational layer across the top. At Bonsai Law, that includes workflow management, smart
          search, SOP help, attendance notes, colleague notifications, matter opening, risk
          capture, completions management, client communications, and live director visibility.
        </p>
        <HairlineList
          testid="caseminder-what-list"
          items={[
            "Conflict check and onboarding pipeline",
            "File opening and risk form with structured risk banding",
            "Source of funds and source of wealth capture",
            "Pre-exchange and pre-completion workflows",
            "Completions Book and post-completion tracking",
            "Smart search across the system",
            "SOP help and operational guidance where people actually need it",
            "Attendance notes and internal notifications",
            "Director's Lounge with live metrics and analytics",
          ]}
        />
      </ContentSection>

      {/* 4. Director's Lounge — feature spotlight */}
      <section className="border-b border-border bg-secondary/40 py-24 md:py-28" data-testid="caseminder-directors-lounge">
        <Container className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline accent>Director's Lounge</Overline>
              <h2 className="mt-5 font-display text-4xl font-semibold leading-tight tracking-tight text-foreground md:text-5xl">
                Visibility for owners, not just workflow for staff
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.08}>
              <p className="leading-relaxed text-muted-foreground">
                CaseMinder includes a director-only oversight layer that makes the firm's metrics
                visible immediately. Pipeline health, fee-earner performance, matter intake trends,
                risk signals, deadline exposure, filing compliance and wider performance can all be
                seen in one place.
              </p>
              <p className="mt-5 text-lg leading-relaxed text-foreground">
                That matters because owners should not have to rely on stitched-together reports to
                understand how their own firm is behaving.
              </p>
              <div className="mt-8 grid grid-cols-2 divide-x divide-border border border-border sm:grid-cols-4">
                {["Pipeline health", "Risk signals", "Deadline exposure", "Fee-earner data"].map(
                  (m, i) => (
                    <div key={i} className="p-5">
                      <span className="overline text-muted-foreground">{m}</span>
                    </div>
                  )
                )}
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* 5. Best-of-class stack */}
      <ContentSection
        n="05"
        eyebrow="Best-of-class stack"
        title="Keep the tools that already do their job well"
        testid="caseminder-stack"
      >
        <p className="leading-relaxed text-muted-foreground">
          CaseMinder is not trying to replace everything with one bloated product. It sits above the
          firm's best-of-class tools and integrates them into one controlled operating layer.
        </p>
        <p className="mt-5 leading-relaxed text-muted-foreground">
          Keep Outlook for email. Keep SharePoint for documents and collaboration. Keep Teams and
          To Do for day-to-day communication and tasks. Integrate billing and finance tools where
          needed. Use CaseMinder as the umbrella across the lot.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          {["Outlook", "SharePoint", "Teams", "To Do", "Billing & finance"].map((t) => (
            <span
              key={t}
              className="border border-border px-4 py-2 text-sm font-medium tracking-wide text-foreground/80"
            >
              {t}
            </span>
          ))}
        </div>
      </ContentSection>

      {/* What's live — capability menu */}
      <section className="border-b border-border bg-secondary/40 py-24 md:py-28" data-testid="caseminder-capabilities">
        <Container>
          <Reveal>
            <div className="max-w-3xl">
              <Overline accent>What's already live</Overline>
              <h2 className="mt-5 font-display text-4xl font-semibold leading-tight tracking-tight text-foreground md:text-5xl">
                A working platform, not a promise.
              </h2>
              <p className="mt-6 leading-relaxed text-muted-foreground">
                A snapshot of what's shipped and in daily use across the firm — and it grows the moment a
                team need is agreed.
              </p>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 border-l border-t border-border sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "New-matter pipeline", d: "Conflict check → matter reference → automatic file structure → onboarding → SRA-weighted risk assessment → client care letters → signed welcome pack." },
              { t: "Outlook add-in & webmail", d: "An Outlook add-in for filing, deadlines and time-recording from email, plus Outlook-grade webmail with even more features — email tied to the right matter." },
              { t: "Conveyancing & completions", d: "Completions book, completion statements, pre-exchange & pre-completion checklists, and client-completed SDLT and TA6 / TA7 / TA10 forms." },
              { t: "Money & oversight", d: "Live matter money bar (quote vs work-in-progress vs money on account), credit control, trust reporting and director dashboards." },
              { t: "Documents & bundles", d: "System-wide matter search, full-text search, inline previews and a court-ready bundle builder." },
              { t: "Every team member", d: "Personalised homepages, 'today' panels, daily digests and an in-app assistant sourced from your own SOPs and live matter data." },
            ].map((c, i) => (
              <div key={i} className="flex h-full flex-col border-b border-r border-border p-6" data-testid={`caseminder-capability-${i}`}>
                <h3 className="font-display text-lg font-bold tracking-[-0.02em] text-foreground">{c.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <FaqSection
        eyebrow="CaseMinder FAQ"
        title="Questions owners ask"
        items={caseminderFaqs}
        testid="caseminder-faq"
        bg
      />

      {/* 7. Adjacent tools / cross-sell to ComplianceMinder */}
      <AdjacentCrossSell
        testid="caseminder-adjacent"
        title="Add live policy and firmwide risk above your workflow"
        body="ComplianceMinder adds the live policy and firmwide risk layer. Connect the two and approved policy changes flow straight into your workflows and prompts — so the firm's day-to-day practice stays aligned with the policies it says it follows."
        to="/complianceminder"
        label="Explore ComplianceMinder"
      />

      {/* 8. Final CTA */}
      <ProductFinalCTA
        testid="caseminder-final-cta"
        title="Build the operating layer your firm actually needs."
        body="Developed with your team, around how you really work — not a vendor template."
        label="Book a CaseMinder walkthrough"
      />
    </div>
  );
}
